import React, { ReactElement } from 'react';

import { Endpoint, ToastService } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import FilterService from '@philips-emr/hpms-provider-commons/lib/services/filter-service';
import { Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color, ToastLevel } from '@philips-emr/tws-constants';
import Controller, { OnClick, OnDynamicContentRender, OnMount, OnSelectItem, OnUnmount } from '@philips-emr/tws-core/lib/client';
import { MountEvent, RenderEvent, SelectItemEvent } from '@philips-emr/tws-core/lib/component/types';
import { HistoryService, HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { AccreditationUpdateAliases as Aliases, FragmentNames } from '../../codegen';
import { Route } from '../../route';
import BaseController from '../BaseController';

interface Accreditation {
  ieStatus: AccreditationStatus;
  dsStatus: string;
  nrSequence: number,
  nrSeqTypeAccreditation: number,
}

interface TypeAccreditation {
  dsAccreditationType: string,
  nrSequence: number,
  message: string,
}

enum AccreditationStatus {
  AWAITING_REQUESTER = '1',
  ANALYSIS = '2',
  AWAITING_REVISION = '3',
  APPROVED = '4',
  CONTRACTED = '5',
  DENIED = '6',
  CHECKING_CNES = '7',
  AWAITING_TECHNICAL_VISIT = '8',
  NEGOTIATING = '9',
  CANCELED = '10'
}

enum AccreditationSteps {
  ESTABLISHMENT = '0',
  EMPLOYEES = '1',
  AMOUNT_PROFESSIONALS = '2',
  SPECIALTIES = '3',
  CLINICAL_STAFF = '4',
  PROCEDURES = '5',
  MEDICINE_MATERIALS = '6',
  OUTSOURCED_SERVICES = '7',
  DOCUMENTS = '8',
  QUESTIONNAIRE = '9',
  BEDS = '10',
  EQUIPMENTS = '11',
  QUALIFICATIONS = '12',
  PACKAGES = '13',
  REGISTER_DATA = '14',
  BANK_DATA = '15',
  SERVICES = '16'
}

@Controller({ fragment: FragmentNames.ACCREDITATION_UPDATE })
class AccreditationUpdateController extends BaseController {

  @OnMount(Aliases.PANEL_ACCREDITATION_UPDATE)
  onMountAccreditationExtension(): void {
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    this.context.set({ nrSeqProvider });
  }

  @OnDynamicContentRender(Aliases.HPMS_CUSTOM_CARD_ACCREDITATIONS)
  OnDynamicContentRenderAccreditations(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsStatus } = data as Accreditation;
    const content = (): ReactElement => (getStampStatusAccreditation(ieStatus, dsStatus));
    render(content);
  }

  @OnClick(Aliases.ACTION_REQUEST_UPDATE)
  onClickActionRequestUpdate(): void {
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    HttpService.get<TypeAccreditation[]>(Endpoint.GET.FETCH_ACCREDITATION_CATEGORIES, { nrSeqProvider })
      .then(({ body }) => {
        if (!isEmptyArrayOrObject(body)) {
          this.historyService.push(Route.ACCREDITATION_UPDATE_CATEGORIES);
        } else {
          ToastService.open({ message: MessageService.translate('provider.provider-not-qualified-accreditation-update'), level: ToastLevel.WARNING });
        }
      }).catch(() => ToastService.open({ message: MessageService.translate('provider.error-process-request'), level: ToastLevel.ERROR }));
  }

  @OnMount(Aliases.FILTER_ACCREDITATION_UPDATE)
  onMountFilterAccreditations(event: MountEvent): void {
    const parameters = {
      dtStart: null,
      dtEnd: null,
    };
    FilterService.restoreFilterState(event, parameters);
  }

  @OnUnmount(Aliases.FILTER_ACCREDITATION_UPDATE)
  onUnmountFilter(event: MountEvent): void {
    FilterService.persistFilterState(event);
  }

  @OnSelectItem(Aliases.DECK_ACCREDITATIONS)
  onSelectItemDeckAccreditations(event: SelectItemEvent<Accreditation>): void {
    const { nrSequence, nrSeqTypeAccreditation } = event.item;
    HistoryService.push(Route.ACCREDITATION_UPDATE_SIDEBAR_MENU, { nrSeqAccreditation: nrSequence, nrSeqTypeAccreditation });
  }
}

function getStampStatusAccreditation(ieStatus: AccreditationStatus, label: string): ReactElement {
  let stampProps = null;

  const icon = {
    [AccreditationStatus.AWAITING_REQUESTER]: { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CLOCK, label },
    [AccreditationStatus.ANALYSIS]: { color: Color.SAFETY_YELLOW, icon: StampIcon.CLOCK, label },
    [AccreditationStatus.AWAITING_REVISION]: { color: Color.SAFETY_ORANGE, icon: StampIcon.WARNING, label },
    [AccreditationStatus.APPROVED]: { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label },
    [AccreditationStatus.CONTRACTED]: { color: Color.SAFETY_BLUE, icon: StampIcon.CHECK, label },
    [AccreditationStatus.DENIED]: { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label },
    [AccreditationStatus.CHECKING_CNES]: { className: 'c-form-stamp__stamp--color', icon: StampIcon.CLOCK, label },
    [AccreditationStatus.AWAITING_TECHNICAL_VISIT]: { color: Color.SAFETY_BLUE, icon: StampIcon.CLOCK, label },
    [AccreditationStatus.NEGOTIATING]: { color: Color.SAFETY_GREEN, icon: StampIcon.CLOCK, label },
    [AccreditationStatus.CANCELED]: { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED, label },
  };

  stampProps = icon[ieStatus];
  return <Stamp {...stampProps} />;
}

export {
  AccreditationUpdateController,
  AccreditationSteps,
  AccreditationStatus,
  TypeAccreditation,
};
